import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSnackbar } from "notistack";
import { Navigate, useLocation, useNavigate } from 'react-router-dom';


interface IWarehousesContext {
    recordCount: number
    search: ISearch
    warehouses: IWarehouse[]
    isLoading: boolean

    doSearch: (search: ISearch) => void
    handleChangePage: (event: any, page: number) => void
    updateSearch: (newSearch: ISearch) => void
    searchWarehouses: (searchRequest: ISearch) => Promise<void>
    deleteWarehouse: (id: string) => Promise<void>
}

export let warehousesContext = React.createContext<IWarehousesContext>({} as IWarehousesContext);

let { Provider } = warehousesContext;

interface IWarehouse {
    id: string
    code: string
    isVirtual: boolean
    warehouseList: string[]
}

export interface ISearch {
    fullText: string
    pageNr: number
    pageSize: number
}

export let WarehousesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    let { enqueueSnackbar } = useSnackbar();
    let location = useLocation();
    let navigate = useNavigate();

    let [isLoading, setLoading] = useState(true);


    let [warehouses, setWarehouses] = useState<IWarehouse[]>([]);
    let [recordCount, setTotalRecords] = useState(0);

    let [search, setSearch] = useState<ISearch>({
        fullText: '',
        pageNr: 0,
        pageSize: 20
    });

    useEffect(() => {
        if (location.state)
            processState(location.state);

    }, [location.state])


    if (!location.state)
        return <Navigate to={location.pathname} state={{ search: search }} replace={true} />


    let processState = (state: any) => {
        if (state) {

            setSearch({
                ...search,
                ...state
            });

            searchWarehouses({
                ...search,
                ...state
            });
        }
    }

    let doSearch = (values: ISearch) => {
        let request = {
            ...values,
            pageNr: values.pageNr > 0 ? 0 : values.pageNr
        }

        navigate(location.pathname, { state: request });
    }

    let updateSearch = (newSearch: ISearch) => {
        setSearch(newSearch);
    }

    let searchWarehouses = async (searchRequest: ISearch) => {
        try {
            setLoading(true);
            let response = await axios.post('/api/Warehouse/SearchWarehouses', searchRequest);

            setWarehouses(response.data.items);
            setTotalRecords(response.data.recordCount);
            setSearch({ ...searchRequest });

        } catch (error) { }
        finally {
            setLoading(false);
        }
    }

    let handleChangePage = (event: any, page: number) => {
        let request = {
            ...search,
            pageNr: page
        }

        window.scrollTo(0, 0);
        navigate(location.pathname, { state: request });
    }

    let deleteWarehouse = async (id: string) => {
        if (window.confirm("Are you sure you want to delete the warehouse?")) {
            try {
                await axios.delete(`/api/Warehouse/DeleteWarehouse/${id}`)
                setWarehouses(warehouses.filter(warehouse => warehouse.id !== id));
                setTotalRecords(recordCount - 1);
                enqueueSnackbar("Warehouse deleted", { variant: 'success' });
            } catch (error) { }
        }
    }


    return (
        <Provider value={{
            recordCount,
            search,
            warehouses,
            isLoading,

            doSearch,
            handleChangePage,
            updateSearch,
            searchWarehouses,
            deleteWarehouse
        }}>
            {children}
        </Provider>
    );
};